import { useEffect } from "react";

import {
  Button,
  InfoPage,
  isLoggedIn,
  LogSentryException,
  ServiceError,
  SpinnerCenter,
} from "@shapr3d/frontend-lib";
import { Outlet, useParams } from "react-router-dom";

import { useHandleViewLinkInvitation } from "../../hooks/useHandleInvitation";
import { useTracker } from "../../hooks/useTracker";
import { useWebExport } from "../../hooks/useWebExport";
import NotFoundPage from "../../pages/NotFoundPage/NotFoundPage";
import { ServiceErrorCode } from "../../types";
import { navigateToAuth } from "../../utils/authUtils";
import { GenericHeader } from "../Headers/GenericHeader/GenericHeader";

import style from "./WebExportPageWrapper.module.css";

export const WebExportPageWrapper = ({ children }: { children?: React.ReactNode }) => {
  const tracker = useTracker();
  const { viewLinkId = "" } = useParams();

  const { isError: isInvitationError } = useHandleViewLinkInvitation();

  const { error: webExportError, isPending: isWebExportPending } = useWebExport();

  useEffect(() => {
    if (webExportError && webExportError instanceof ServiceError) {
      if (webExportError.hasCode(ServiceErrorCode.Forbidden)) {
        tracker.trackViewLinkAccessDenied({ viewLinkId, reason: "missing_access" });
        return;
      }
      if (webExportError.hasCode(ServiceErrorCode.AuthRequired)) {
        tracker.trackViewLinkAccessDenied({ viewLinkId, reason: "unauthenticated" });
        navigateToAuth({ shouldRelogin: true });
        return;
      }
      if (!webExportError.hasCode(ServiceErrorCode.InvalidRequestQueryParameter)) {
        LogSentryException({ messagePrefix: "Web export download error", error: webExportError });
      }
    }
  }, [webExportError, tracker, viewLinkId]);

  if (isInvitationError) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <InfoPage
          variant="warning"
          title={
            <>
              Something went wrong,
              <br />
              please try again.
            </>
          }
          primaryCTA={
            <Button
              onClick={() => {
                window.location.replace(window.location.href);
              }}
            >
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  if (
    isWebExportPending ||
    (webExportError instanceof ServiceError &&
      webExportError.hasCode(ServiceErrorCode.AuthRequired))
  ) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <SpinnerCenter />
      </div>
    );
  }

  if (
    webExportError instanceof ServiceError &&
    webExportError?.hasCode(ServiceErrorCode.Forbidden) &&
    isLoggedIn()
  ) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <InfoPage
          variant="information"
          title="You don't have permission to open this design"
          description="You may try to log in with a different account."
          primaryCTA={
            <Button
              onClick={() => {
                navigateToAuth({ shouldRelogin: true });
              }}
            >
              Back to log in
            </Button>
          }
        />
      </div>
    );
  }

  if (webExportError) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <NotFoundPage />
      </div>
    );
  }

  return children || <Outlet />;
};
